import { z } from 'zod'
import {
  BindModeSchema,
  CommandTypeSchema,
  CoordsSchema,
  FrameworkSchema,
  FRAMEWORKS,
  BIND_MODES,
  COMMAND_TYPES,
  type BindMode,
  type CommandType,
  type Coords,
  type Framework,
} from './constants.js'

import { ActionSchema, DETECTION_KEYS, DetectionKeySchema, SeveritySchema, SideSchema } from './detections.js'
import { HardIdentifierTypeSchema, IdentifiersSchema, IdentifierTypeSchema, TokensSchema } from './identifiers.js'
import { ApiKeyScopeSchema, MemberStatusSchema, RoleSchema } from './roles.js'

/**
 * SPEC.md §4.3 / docs/API.md — the frozen wire contract of the panel REST API.
 *
 * Every field name here comes straight from `docs/API.md`; renaming one is a WP0
 * change. Frozen names that are easy to get wrong: `playerSource` (never
 * `source`) for an in-game id, `detectionKey` for the enum, `detectionId` for a
 * detection row uuid, `banCode` (cloud-minted) vs `agentBanCode` (the
 * provisional code the agent showed the player), and `{ items, nextCursor }`
 * for every list.
 *
 * Request schemas are strict — an unknown key is a `VALIDATION_ERROR` rather
 * than a silently ignored typo. Response/`*Dto` schemas describe output and
 * stay permissive about future additive fields.
 *
 * Enums, role/permission and identifier vocabularies are imported from their
 * owning modules (`detections.ts`, `identifiers.ts`, `roles.ts`, `errors.ts`)
 * and are never redeclared here.
 */

// ───────────────────────────── Shared primitives ─────────────────────────────

/** Lowercase uuid (API.md §1.1). */
const uuid = z.uuid()
/** ISO-8601 UTC timestamp, e.g. `2026-09-07T12:00:00.000Z` (API.md §1.1). */
const isoDateTime = z.iso.datetime()
/** `inet` column; either family. */
const ipAddress = z.union([z.ipv4(), z.ipv6()])
/** Free text a human typed. Bounded so a body cannot be used as storage. */
const shortText = z.string().max(200)
const label = z.string().max(120)
const reasonText = z.string().max(500)
/** Opaque JSON bag (`evidence`, `result`, audit `before`/`after`). */
const jsonObject = z.record(z.string(), z.unknown())
/** FiveM in-game player id. Never called `source` on the wire. */
const playerSource = z.number().int().min(1).max(4096)
/** `-1` broadcasts to everyone (API.md §15, `message` payload only). */
const broadcastSource = z.union([z.literal(-1), playerSource])
/** A single free-text `type:value` identifier, as query parameters carry it. */
const identifierExpr = z.string().min(3).max(160)

/** SPEC.md §3.1 `server_status`. */
export const SERVER_STATUSES = ['never', 'online', 'offline'] as const
export const ServerStatusSchema = z.enum(SERVER_STATUSES)
export type ServerStatus = z.infer<typeof ServerStatusSchema>

/** SPEC.md §3.2 `servers.framework`, mirrors `config.general.framework`. */

/** SPEC.md §3.1 `global_bans_mode`. Read-only mirror of `config.connect.globalBans`. */
export const GLOBAL_BANS_MODES = ['block', 'flag', 'off'] as const
export const GlobalBansModeSchema = z.enum(GLOBAL_BANS_MODES)
export type GlobalBansMode = z.infer<typeof GlobalBansModeSchema>

/** SPEC.md §3.1 `license_status`. */
export const LICENSE_STATUSES = ['active', 'suspended', 'revoked'] as const
export const LicenseStatusSchema = z.enum(LICENSE_STATUSES)
export type LicenseStatus = z.infer<typeof LicenseStatusSchema>

/** SPEC.md §3.1 `bind_mode`. `none` is dev-only and caps the server at 8 players. */

/** SPEC.md §3.1 `ban_source`. */
export const BAN_SOURCES = ['system', 'panel', 'export', 'command', 'import', 'evasion'] as const
export const BanSourceSchema = z.enum(BAN_SOURCES)
export type BanSource = z.infer<typeof BanSourceSchema>

/** SPEC.md §3.1 `global_status`. */
export const GLOBAL_STATUSES = ['none', 'proposed', 'approved', 'rejected', 'revoked'] as const
export const GlobalStatusSchema = z.enum(GLOBAL_STATUSES)
export type GlobalStatus = z.infer<typeof GlobalStatusSchema>

/** SPEC.md §3.1 `command_type`. */

/** SPEC.md §3.1 `command_status`. */
export const COMMAND_STATUSES = ['queued', 'delivered', 'acked', 'failed', 'expired'] as const
export const CommandStatusSchema = z.enum(COMMAND_STATUSES)
export type CommandStatus = z.infer<typeof CommandStatusSchema>

/** SPEC.md §3.1 `screenshot_status`. */
export const SCREENSHOT_STATUSES = ['pending', 'uploaded', 'failed', 'expired'] as const
export const ScreenshotStatusSchema = z.enum(SCREENSHOT_STATUSES)
export type ScreenshotStatus = z.infer<typeof ScreenshotStatusSchema>

/** SPEC.md §3.1 `delivery_status` (`webhook_deliveries`). */
export const DELIVERY_STATUSES = ['queued', 'sent', 'failed', 'dead'] as const
export const DeliveryStatusSchema = z.enum(DELIVERY_STATUSES)
export type DeliveryStatus = z.infer<typeof DeliveryStatusSchema>

/** SPEC.md §3.1 `actor_type` (`audit_logs.actor_type`, `commands.created_by_type`). */
export const ACTOR_TYPES = ['user', 'api_key', 'agent', 'system'] as const
export const ActorTypeSchema = z.enum(ACTOR_TYPES)
export type ActorType = z.infer<typeof ActorTypeSchema>

/** SPEC.md §3.1 `webhook_event`. */
export const WEBHOOK_EVENTS = [
  'detection',
  'ban',
  'unban',
  'kick',
  'player_join',
  'server_status',
  'command_result',
  'screenshot_ready',
  'global_ban_match',
] as const
export const WebhookEventSchema = z.enum(WEBHOOK_EVENTS)
export type WebhookEvent = z.infer<typeof WebhookEventSchema>

/** Panel UI language (API.md §3, `users.locale`). */
export const LOCALES = ['tr', 'en'] as const
export const LocaleSchema = z.enum(LOCALES)
export type Locale = z.infer<typeof LocaleSchema>

/** Aggregation window for `…/stats/overview` and `…/detections/summary` (API.md §9, §18). */
export const STATS_RANGES = ['24h', '7d', '30d'] as const
export const StatsRangeSchema = z.enum(STATS_RANGES)
export type StatsRange = z.infer<typeof StatsRangeSchema>

/** Bucket width the server picked for a `StatsRange`: 15 m / 1 h / 6 h. */
export const STATS_BUCKETS = ['15m', '1h', '6h'] as const
export const StatsBucketSchema = z.enum(STATS_BUCKETS)
export type StatsBucket = z.infer<typeof StatsBucketSchema>

/**
 * `player_identifiers.type` / `ban_identifiers.type` (SPEC.md §3.2) — the six
 * `IDENTIFIER_TYPES` plus the opaque hardware `token` rows, which exist only in
 * those two tables and never inside an `Identifiers` object.
 */
export const PlayerIdentifierTypeSchema = z.union([IdentifierTypeSchema, z.literal('token')])
export type PlayerIdentifierType = z.infer<typeof PlayerIdentifierTypeSchema>

// ─────────────────────── Pagination and list query fragment ───────────────────
// API.md §1.5 — every list endpoint answers `Paginated<T>`, keyset paged.

/**
 * SPEC.md §4.3. Wraps an item schema in the one pagination envelope the whole
 * API uses. `nextCursor` is `null` only on the last page.
 */
export const paginated = <T extends z.ZodTypeAny>(item: T) =>
  z.object({ items: z.array(item), nextCursor: z.string().nullable() })

export interface Paginated<T> {
  items: T[]
  nextCursor: string | null
}

/** API.md §1.5: `limit` 1–200 (default 50) — an out-of-range value is a `VALIDATION_ERROR`, never a silent clamp. */
export const LIMIT_MIN = 1
export const LIMIT_MAX = 200
export const LIMIT_DEFAULT = 50

/**
 * The cursor/limit fragment every list endpoint mixes in. `cursor` is an opaque
 * base64url blob (`<sortValue>|<id>`); clients must not decode it, and it is not
 * portable across filter sets.
 */
export const ListQuerySchema = z.object({
  cursor: z.string().max(512).optional(),
  limit: z.coerce.number().int().min(LIMIT_MIN).max(LIMIT_MAX).default(LIMIT_DEFAULT),
})
export type ListQuery = z.infer<typeof ListQuerySchema>

/** Inclusive ISO-8601 bounds applied to the endpoint's ordering column (API.md §1.5). */
export const DateRangeQuerySchema = z.object({
  from: isoDateTime.optional(),
  to: isoDateTime.optional(),
})
export type DateRangeQuery = z.infer<typeof DateRangeQuerySchema>

/** A repeatable query parameter: one value or many, OR-combined inside its own group. */
const repeatable = <T extends z.ZodTypeAny>(item: T) => z.union([item, z.array(item).max(64)])

/** `?flag=true|false|1|0` — never `z.coerce.boolean()`, which makes `"false"` truthy. */
const boolParam = z.stringbool()

// ───────────────────────────── §2. Authentication ─────────────────────────────
// API.md §2 — `routes/panel/auth.ts`. Access tokens are EdDSA, `aud:'panel'`,
// TTL 900 s and memory-only in the panel; the refresh token is the `sac_rt`
// HttpOnly cookie and never appears in a body.

/** API.md §2 `POST /v1/auth/register`. Password minimum is 10 characters (bcryptjs cost 12). */
export const RegisterRequestSchema = z.strictObject({
  email: z.email().max(254).trim().toLowerCase(),
  password: z.string().min(10).max(200),
  displayName: z.string().min(1).max(80).trim(),
  locale: LocaleSchema.default('tr'),
})
export type RegisterRequest = z.infer<typeof RegisterRequestSchema>

/** API.md §2 `POST /v1/auth/login`. Failures are always generic — the email is never confirmed. */
export const LoginRequestSchema = z.strictObject({
  email: z.email().max(254).trim().toLowerCase(),
  password: z.string().min(1).max(200),
})
export type LoginRequest = z.infer<typeof LoginRequestSchema>

/** API.md §2 — `users` row as the panel sees it. Never carries `password_hash` or `session_version`. */
/**
 * The commercial plans, as the API reports them.
 *
 * A closed enum here and text in the database: adding a plan is then a
 * deployment of both sides rather than a migration, and an account carrying a
 * plan this build has never heard of degrades to `starter` instead of failing
 * to load the session.
 */
export const PLAN_IDS = ['starter', 'pro', 'enterprise'] as const
export const PlanIdSchema = z.enum(PLAN_IDS)
export type PlanId = z.infer<typeof PlanIdSchema>

export const UserDtoSchema = z.object({
  id: uuid,
  email: z.email().max(254),
  displayName: z.string().max(80),
  locale: LocaleSchema,
  isPlatformAdmin: z.boolean(),
  plan: PlanIdSchema.catch('starter'),
  lastLoginAt: isoDateTime.nullable(),
  createdAt: isoDateTime,
})
export type UserDto = z.infer<typeof UserDtoSchema>

/** API.md §2 — the body of register / login / refresh. The refresh token travels only as `Set-Cookie`. */
export const AuthResponseSchema = z.object({
  accessToken: z.string().min(16).max(4096),
  /** Access-token lifetime in seconds (900). */
  expiresIn: z.number().int().min(1).max(86400),
  user: UserDtoSchema,
})
export type AuthResponse = z.infer<typeof AuthResponseSchema>

/** API.md §2 `GET /v1/auth/me` — the trimmed server projection behind the ServerSwitcher. */
export const MeServerSummarySchema = z.object({
  id: uuid,
  name: z.string().max(80),
  role: RoleSchema,
  status: ServerStatusSchema,
  playersOnline: z.number().int().min(0).max(4096),
})
export type MeServerSummary = z.infer<typeof MeServerSummarySchema>

/** API.md §2 `GET /v1/auth/me` — the `AppShell` session bootstrap. */
export const MeResponseSchema = z.object({
  user: UserDtoSchema,
  servers: z.array(MeServerSummarySchema).max(200),
})
export type MeResponse = z.infer<typeof MeResponseSchema>

/** API.md §2 `POST /v1/auth/password/forgot` — always answers `204`, so no user enumeration. */
export const PasswordForgotRequestSchema = z.strictObject({
  email: z.email().max(254).trim().toLowerCase(),
})
export type PasswordForgotRequest = z.infer<typeof PasswordForgotRequestSchema>

/** API.md §2 `POST /v1/auth/password/reset` — consumes the `email_tokens` row and bumps `session_version`. */
export const PasswordResetRequestSchema = z.strictObject({
  token: z.string().min(16).max(256),
  password: z.string().min(10).max(200),
})
export type PasswordResetRequest = z.infer<typeof PasswordResetRequestSchema>

/** API.md §3 `POST /v1/users/me/password` — logs every session out, the caller's included. */
export const PasswordChangeRequestSchema = z.strictObject({
  currentPassword: z.string().min(1).max(200),
  newPassword: z.string().min(10).max(200),
})
export type PasswordChangeRequest = z.infer<typeof PasswordChangeRequestSchema>

/** API.md §3 `PATCH /v1/users/me`. The email is immutable in v1. */
export const UserUpdateRequestSchema = z
  .strictObject({
    displayName: z.string().min(1).max(80).trim().optional(),
    locale: LocaleSchema.optional(),
  })
  .refine((v) => v.displayName !== undefined || v.locale !== undefined, {
    message: 'At least one field must be provided',
  })
export type UserUpdateRequest = z.infer<typeof UserUpdateRequestSchema>

/** API.md §3 `PATCH /v1/users/me` response. */
export const UserResponseSchema = z.object({ user: UserDtoSchema })
export type UserResponse = z.infer<typeof UserResponseSchema>

// ─────────────────────────────── §4. API keys ────────────────────────────────
// API.md §4 — personal keys belong to a user, are scope-limited and read-mostly.
// The effective permission set is the intersection of the user's role and the
// key's scopes.

/** API.md §4 — never contains key material; only `keyId` and `last4` survive creation. */
export const ApiKeyDtoSchema = z.object({
  id: uuid,
  name: label,
  /** Public 8-char base62 half of the key, used to find the row. */
  keyId: z.string().regex(/^[0-9A-Za-z]{8}$/),
  /** Last four characters of the plaintext key, for visual identification only. */
  last4: z.string().min(4).max(8),
  scopes: z.array(ApiKeyScopeSchema).max(16),
  /** `null` = every server the owning user can access. */
  serverIds: z.array(uuid).max(200).nullable(),
  expiresAt: isoDateTime.nullable(),
  lastUsedAt: isoDateTime.nullable(),
  lastUsedIp: ipAddress.nullable(),
  revokedAt: isoDateTime.nullable(),
  createdAt: isoDateTime,
})
export type ApiKeyDto = z.infer<typeof ApiKeyDtoSchema>

/** API.md §4 `POST /v1/users/me/api-keys`. `serverIds` omitted = every accessible server. */
export const ApiKeyCreateRequestSchema = z.strictObject({
  name: label.min(1),
  scopes: z.array(ApiKeyScopeSchema).min(1).max(16),
  serverIds: z.array(uuid).max(200).optional(),
  expiresAt: isoDateTime.optional(),
})
export type ApiKeyCreateRequest = z.infer<typeof ApiKeyCreateRequestSchema>

/**
 * API.md §4 — `key` is the full `sacp_<keyId>_<secret><crc>` value and is
 * returned **exactly once**, at creation. It is stored only as a sha256 hash;
 * no endpoint can ever reveal it again.
 */
export const ApiKeyCreateResponseSchema = z.object({
  key: z.string().startsWith('sacp_').min(24).max(200),
  apiKey: ApiKeyDtoSchema,
})
export type ApiKeyCreateResponse = z.infer<typeof ApiKeyCreateResponseSchema>

// ──────────────────────────────── §5. Servers ────────────────────────────────
// API.md §5 — `routes/panel/servers.ts`. Agent-reported fields (`status`,
// `playersOnline`, `agentConfigVersion`, `reputation`, `globalBansMode`) are
// read-only here; `globalBansMode` changes through the config `PUT`.

/** The license summary embedded in `ServerDto` — masked identifiers only, never key material. */
export const ServerLicenseSummarySchema = z.object({
  id: uuid,
  keyId: z.string().regex(/^[0-9A-Za-z]{8}$/),
  last4: z.string().min(4).max(8),
  status: LicenseStatusSchema,
  bindMode: BindModeSchema,
  boundIp: ipAddress.nullable(),
  graceUntil: isoDateTime.nullable(),
  lastAuthAt: isoDateTime.nullable(),
})
export type ServerLicenseSummary = z.infer<typeof ServerLicenseSummarySchema>

/** API.md §5 `GET /v1/servers`. `role` is the *caller's* role on that server. */
export const ServerDtoSchema = z.object({
  id: uuid,
  name: z.string().max(80),
  slug: z.string().regex(/^[a-z0-9-]{1,80}$/),
  framework: FrameworkSchema,
  timezone: z.string().min(1).max(64),
  status: ServerStatusSchema,
  lastHeartbeatAt: isoDateTime.nullable(),
  agentVersion: z.string().max(32).nullable(),
  agentOutdated: z.boolean(),
  agentIp: ipAddress.nullable(),
  playersOnline: z.number().int().min(0).max(4096),
  maxPlayers: z.number().int().min(0).max(4096),
  uptimeSeconds: z.number().int().min(0).max(31_536_000),
  configVersion: z.number().int().min(0).max(1_000_000),
  /** Version the agent last reported applied; `configVersion` when in sync. */
  agentConfigVersion: z.number().int().min(0).max(1_000_000),
  banListVersion: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
  /** Read-only mirror of `config.connect.globalBans` — change it through the config `PUT`. */
  globalBansMode: GlobalBansModeSchema,
  globalBansContribute: z.boolean(),
  /** Reporter reputation, clamped 0–100; gates global-ban proposals. */
  reputation: z.number().int().min(0).max(100),
  plan: z.string().max(32),
  detectionsRetentionDays: z.number().int().min(1).max(3650),
  screenshotsRetentionDays: z.number().int().min(1).max(3650),
  role: RoleSchema,
  license: ServerLicenseSummarySchema.nullable(),
  createdAt: isoDateTime,
})
export type ServerDto = z.infer<typeof ServerDtoSchema>

/** API.md §5 `GET /v1/servers/:serverId`, `PATCH …`. */
export const ServerResponseSchema = z.object({ server: ServerDtoSchema })
export type ServerResponse = z.infer<typeof ServerResponseSchema>

/** API.md §5 `POST /v1/servers`. The slug is derived server-side and may collide (`409 CONFLICT`). */
export const ServerCreateRequestSchema = z.strictObject({
  name: z.string().min(1).max(80).trim(),
  framework: FrameworkSchema.default('standalone'),
  timezone: z.string().min(1).max(64).default('Europe/Istanbul'),
})
export type ServerCreateRequest = z.infer<typeof ServerCreateRequestSchema>

/**
 * API.md §5 `PATCH /v1/servers/:serverId`. Only these fields are writable —
 * `globalBansMode`, `configVersion`, `status`, `reputation` and every
 * agent-reported field are read-only. Changing `framework` also rewrites
 * `config.general.framework`, producing a new config version.
 */
export const ServerUpdateRequestSchema = z.strictObject({
  name: z.string().min(1).max(80).trim().optional(),
  framework: FrameworkSchema.optional(),
  timezone: z.string().min(1).max(64).optional(),
  globalBansContribute: z.boolean().optional(),
  detectionsRetentionDays: z.number().int().min(1).max(3650).optional(),
  screenshotsRetentionDays: z.number().int().min(1).max(3650).optional(),
})
export type ServerUpdateRequest = z.infer<typeof ServerUpdateRequestSchema>

/** API.md §5 `DELETE /v1/servers/:serverId` — soft delete, guarded by retyping the name. */
export const ServerDeleteRequestSchema = z.strictObject({
  confirmName: z.string().min(1).max(80),
})
export type ServerDeleteRequest = z.infer<typeof ServerDeleteRequestSchema>

/** API.md §5 `POST /v1/servers/:serverId/transfer` — the target must already be an active member. */
export const ServerTransferRequestSchema = z.strictObject({ userId: uuid })
export type ServerTransferRequest = z.infer<typeof ServerTransferRequestSchema>

/**
 * API.md §5 `GET /v1/servers/:serverId/install`.
 *
 * `licenseKeyMasked` is the display form `sacl_7Kp2Qm9X_…Q4F7`; the plaintext
 * key is **never** returned here — only `POST /v1/servers`, `POST …/licenses`
 * and `POST …/licenses/rotate` ever emit it, and never again afterwards.
 * `serverCfgLines` use `set` (never `setr`, which would replicate the license
 * convar to every client — SPEC §8.1).
 */
export const InstallInfoSchema = z.object({
  serverId: uuid,
  resourceDownloadUrl: z.url().max(2048),
  resourceVersion: z.string().max(32),
  minAgentVersion: z.string().max(32),
  apiUrl: z.url().max(2048),
  licenseKeyMasked: z.string().max(128),
  serverCfgLines: z.array(z.string().max(500)).max(40),
  configLuaSnippet: z.string().max(4000),
  steps: z.array(z.string().max(500)).max(20),
})
export type InstallInfo = z.infer<typeof InstallInfoSchema>

// ─────────────────────── §6. Members and invites ─────────────────────────────
// API.md §6 — exactly one `owner` row exists per server at all times; the owner
// role only moves through `POST …/transfer`, never through these routes.

/** API.md §6 `GET /v1/servers/:serverId/members`. */
export const MemberDtoSchema = z.object({
  id: uuid,
  userId: uuid,
  email: z.email().max(254),
  displayName: z.string().max(80),
  role: RoleSchema,
  status: MemberStatusSchema,
  invitedBy: uuid.nullable(),
  createdAt: isoDateTime,
  acceptedAt: isoDateTime.nullable(),
})
export type MemberDto = z.infer<typeof MemberDtoSchema>

/** Roles that may be assigned or invited. `owner` is excluded — it moves only through transfer. */
export const AssignableRoleSchema = z.enum(['admin', 'moderator', 'viewer'])
export type AssignableRole = z.infer<typeof AssignableRoleSchema>

/** API.md §6 `PATCH …/members/:memberId`. An admin may not promote to admin; only the owner can. */
export const MemberUpdateRequestSchema = z.strictObject({ role: AssignableRoleSchema })
export type MemberUpdateRequest = z.infer<typeof MemberUpdateRequestSchema>

export const MemberResponseSchema = z.object({ member: MemberDtoSchema })
export type MemberResponse = z.infer<typeof MemberResponseSchema>

/** API.md §5 `POST …/transfer` — both rows after the swap. */
export const MemberListResponseSchema = z.object({ members: z.array(MemberDtoSchema).max(500) })
export type MemberListResponse = z.infer<typeof MemberListResponseSchema>

/** API.md §6 — pending rows only; only `token_hash` is stored, never the token. */
export const InviteDtoSchema = z.object({
  id: uuid,
  email: z.email().max(254),
  role: RoleSchema,
  expiresAt: isoDateTime,
  acceptedAt: isoDateTime.nullable(),
  createdAt: isoDateTime,
})
export type InviteDto = z.infer<typeof InviteDtoSchema>

/** API.md §6 `POST …/invites`. `role` may not be `owner`, and an admin may not invite an admin. */
export const InviteRequestSchema = z.strictObject({
  email: z.email().max(254).trim().toLowerCase(),
  role: AssignableRoleSchema,
})
export type InviteRequest = z.infer<typeof InviteRequestSchema>

/**
 * API.md §6 `POST …/invites` and `…/invites/:inviteId/resend`.
 * `inviteUrl` carries the raw token and is returned **once** — the row keeps
 * only `token_hash`, so the URL cannot be recovered later (resend rotates it).
 */
export const InviteResponseSchema = z.object({
  invite: InviteDtoSchema,
  inviteUrl: z.url().max(2048),
})
export type InviteResponse = z.infer<typeof InviteResponseSchema>

/** API.md §2 `POST /v1/invites/accept`. */
export const InviteAcceptRequestSchema = z.strictObject({ token: z.string().min(16).max(256) })
export type InviteAcceptRequest = z.infer<typeof InviteAcceptRequestSchema>

export const InviteAcceptResponseSchema = z.object({
  server: ServerDtoSchema,
  member: MemberDtoSchema,
})
export type InviteAcceptResponse = z.infer<typeof InviteAcceptResponseSchema>

// ─────────────────────────────── §7. Licenses ────────────────────────────────
// API.md §7 — owner only (`license.manage`). Format
// `sacl_<keyId 8 base62>_<secret 32 base62><crc 6 base62>`.

/**
 * API.md §7. Displayable: `keyId`, `last4`, the masked form, status, bind mode,
 * `boundIp`, `graceUntil` and the auth counters. **Never** returned in any
 * response: the secret, the `mac_key`, or any hash. There is no reveal
 * endpoint — an owner who lost the key rotates instead.
 */
export const LicenseDtoSchema = z.object({
  id: uuid,
  serverId: uuid,
  keyId: z.string().regex(/^[0-9A-Za-z]{8}$/),
  last4: z.string().min(4).max(8),
  status: LicenseStatusSchema,
  bindMode: BindModeSchema,
  boundIp: ipAddress.nullable(),
  boundAt: isoDateTime.nullable(),
  ipResetCount: z.number().int().min(0).max(100_000),
  ipResetAt: isoDateTime.nullable(),
  expiresAt: isoDateTime.nullable(),
  /** The license this one replaced, set by `POST …/licenses/rotate`. */
  supersedesId: uuid.nullable(),
  /** Dual-accept window after a rotation; both keys authenticate until it passes. */
  graceUntil: isoDateTime.nullable(),
  lastAuthAt: isoDateTime.nullable(),
  lastAuthIp: ipAddress.nullable(),
  authCount: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
  suspendedAt: isoDateTime.nullable(),
  suspendedReason: reasonText.nullable(),
  revokedAt: isoDateTime.nullable(),
  revokedReason: reasonText.nullable(),
  createdAt: isoDateTime,
})
export type LicenseDto = z.infer<typeof LicenseDtoSchema>

export const LicenseResponseSchema = z.object({ license: LicenseDtoSchema })
export type LicenseResponse = z.infer<typeof LicenseResponseSchema>

/**
 * API.md §5/§7 — the only three places a plaintext license key exists:
 * `POST /v1/servers`, `POST …/licenses` and `POST …/licenses/rotate`.
 *
 * `key` is returned **exactly once** and never persisted in clear (only
 * `key_hash` and `mac_key` are stored), so no later read can reproduce it.
 */
export const LicenseRevealResponseSchema = z.object({
  key: z.string().startsWith('sacl_').min(24).max(200),
  license: LicenseDtoSchema,
})
export type LicenseRevealResponse = z.infer<typeof LicenseRevealResponseSchema>

/** API.md §7 `POST …/licenses/rotate` — adds the superseded key's grace window. */
export const LicenseRotateResponseSchema = LicenseRevealResponseSchema.extend({
  previous: z.object({
    id: uuid,
    keyId: z.string().regex(/^[0-9A-Za-z]{8}$/),
    graceUntil: isoDateTime,
  }),
})
export type LicenseRotateResponse = z.infer<typeof LicenseRotateResponseSchema>

/** API.md §7 `POST …/licenses` — only for a server with no active license. */
export const LicenseCreateRequestSchema = z.strictObject({
  bindMode: BindModeSchema.default('first_use'),
})
export type LicenseCreateRequest = z.infer<typeof LicenseCreateRequestSchema>

/** API.md §7 `PATCH …/licenses/:licenseId`. `boundIp` is required when `bindMode` is `manual`. */
export const LicenseUpdateRequestSchema = z
  .strictObject({
    bindMode: BindModeSchema.optional(),
    boundIp: ipAddress.nullable().optional(),
    expiresAt: isoDateTime.nullable().optional(),
  })
  .refine((v) => v.bindMode !== 'manual' || (v.boundIp !== null && v.boundIp !== undefined), {
    message: 'boundIp is required when bindMode is "manual"',
    path: ['boundIp'],
  })
export type LicenseUpdateRequest = z.infer<typeof LicenseUpdateRequestSchema>

/** API.md §7 `POST …/licenses/:licenseId/suspend` — reversible. */
export const LicenseSuspendRequestSchema = z.strictObject({ reason: reasonText.min(1) })
export type LicenseSuspendRequest = z.infer<typeof LicenseSuspendRequestSchema>

/** API.md §7 `POST …/licenses/:licenseId/revoke` — irreversible. */
export const LicenseRevokeRequestSchema = z.strictObject({ reason: reasonText.min(1) })
export type LicenseRevokeRequest = z.infer<typeof LicenseRevokeRequestSchema>

// ───────────────────────────── §8. Server config ─────────────────────────────
// API.md §8 — every write inserts a new immutable `server_configs` row and is
// guarded by `baseVersion` (optimistic concurrency, `409
// CONFIG_VERSION_CONFLICT`).

/**
 * The `ServerConfig` blob. Its real shape is `ServerConfigSchema` (SPEC §4.2,
 * `config.ts`) — kept opaque here so the wire contract does not depend on the
 * config module, which the API validates separately and rejects with
 * `CONFIG_INVALID`.
 */
export const ServerConfigBlobSchema = z.looseObject({
  schemaVersion: z.number().int().min(1).max(1000),
})
export type ServerConfigBlob = z.infer<typeof ServerConfigBlobSchema>

/** Who authored a config version; `null` when the system did (server creation, framework rewrite). */
export const ConfigAuthorSchema = z.object({ id: uuid, displayName: z.string().max(80) })
export type ConfigAuthor = z.infer<typeof ConfigAuthorSchema>

/** API.md §8 `GET …/config/versions` — history without the blob. */
export const ConfigVersionSummarySchema = z.object({
  version: z.number().int().min(1).max(1_000_000),
  checksum: z.string().max(128),
  note: shortText.nullable(),
  createdBy: ConfigAuthorSchema.nullable(),
  createdAt: isoDateTime,
})
export type ConfigVersionSummary = z.infer<typeof ConfigVersionSummarySchema>

/**
 * API.md §8 `GET …/config`, `GET …/config/versions/:version`, `PUT …/config`.
 * `appliedVersion` mirrors `servers.agent_config_version`; `synced` is
 * `appliedVersion === version`.
 */
export const ConfigVersionDtoSchema = ConfigVersionSummarySchema.extend({
  schemaVersion: z.number().int().min(1).max(1000),
  appliedVersion: z.number().int().min(0).max(1_000_000),
  synced: z.boolean(),
  config: ServerConfigBlobSchema,
})
export type ConfigVersionDto = z.infer<typeof ConfigVersionDtoSchema>

/** API.md §8 `PUT …/config`. `baseVersion` must equal the current `servers.config_version`. */
export const ConfigPutRequestSchema = z.strictObject({
  baseVersion: z.number().int().min(0).max(1_000_000),
  note: shortText.optional(),
  config: ServerConfigBlobSchema,
})
export type ConfigPutRequest = z.infer<typeof ConfigPutRequestSchema>

/**
 * API.md §8 `PATCH …/config/modules/:detectionKey` — the single-module save
 * behind the panel's optimistic toggles. It still carries `baseVersion` and
 * still produces a full new config version. Changing `action` on a module whose
 * `DETECTION_META[key].lockedAction` is set (`menu_key`, `connect_offline`,
 * `client_integrity`) is a `VALIDATION_ERROR`.
 */
export const ModulePatchRequestSchema = z.strictObject({
  baseVersion: z.number().int().min(0).max(1_000_000),
  enabled: z.boolean().optional(),
  action: ActionSchema.optional(),
  strikes: z.number().int().min(1).max(1000).optional(),
  windowMs: z.number().int().min(0).max(3_600_000).optional(),
  screenshot: z.boolean().optional(),
  /** `0` = permanent ban when this module trips. */
  banDurationSec: z.number().int().min(0).max(31_536_000).optional(),
  options: jsonObject.optional(),
})
export type ModulePatchRequest = z.infer<typeof ModulePatchRequestSchema>

/** The single module as `PATCH …/config/modules/:detectionKey` echoes it back, merged with its defaults. */
export const ConfigModuleDtoSchema = z.object({
  detectionKey: DetectionKeySchema,
  enabled: z.boolean(),
  action: ActionSchema,
  strikes: z.number().int().min(0).max(1000),
  windowMs: z.number().int().min(0).max(3_600_000),
  screenshot: z.boolean(),
  banDurationSec: z.number().int().min(0).max(31_536_000),
  options: jsonObject,
})
export type ConfigModuleDto = z.infer<typeof ConfigModuleDtoSchema>

/** API.md §8 `PATCH …/config/modules/:detectionKey` response. */
export const ModulePatchResponseSchema = z.object({
  version: z.number().int().min(1).max(1_000_000),
  checksum: z.string().max(128),
  appliedVersion: z.number().int().min(0).max(1_000_000),
  synced: z.boolean(),
  module: ConfigModuleDtoSchema,
})
export type ModulePatchResponse = z.infer<typeof ModulePatchResponseSchema>

/** API.md §8 `POST …/config/rollback` — history is append-only; this inserts a copy of `version`. */
export const ConfigRollbackRequestSchema = z.strictObject({
  version: z.number().int().min(1).max(1_000_000),
  baseVersion: z.number().int().min(0).max(1_000_000),
})
export type ConfigRollbackRequest = z.infer<typeof ConfigRollbackRequestSchema>

/** API.md §8 `POST …/config/reset` — back to `DEFAULT_SERVER_CONFIG`, keeping `general.framework`. */
export const ConfigResetRequestSchema = z.strictObject({
  baseVersion: z.number().int().min(0).max(1_000_000),
})
export type ConfigResetRequest = z.infer<typeof ConfigResetRequestSchema>

/** API.md §8 `POST …/config/import` — the payload runs through `migrateConfig()` first. */
export const ConfigImportRequestSchema = z.strictObject({
  baseVersion: z.number().int().min(0).max(1_000_000),
  config: ServerConfigBlobSchema,
  note: shortText.optional(),
})
export type ConfigImportRequest = z.infer<typeof ConfigImportRequestSchema>

// ────────────────────────────── §9. Detections ───────────────────────────────
// API.md §9 — detections are never created through the panel; only the agent
// ingests them. Ordering is `received_at DESC, id DESC`.

/** Server-sourced world position attached to a detection. */

/**
 * API.md §9 `GET …/detections`. `detectionId` is this row's uuid; `detectionKey`
 * is the enum; `playerSource` is the in-game id (never `source`).
 */
export const DetectionDtoSchema = z.object({
  id: uuid,
  serverId: uuid,
  /** Agent idempotency id `<bootId>-<seq>`, unique per `(server_id, event_id)`. */
  eventId: z.string().max(128),
  batchId: z.string().max(128).nullable(),
  detectionKey: DetectionKeySchema,
  side: SideSchema,
  severity: SeveritySchema,
  actionTaken: ActionSchema,
  actionExecuted: z.boolean(),
  serverConfirmed: z.boolean(),
  strikes: z.number().int().min(0).max(10_000).nullable(),
  /** Evidence weight 0–100. */
  score: z.number().int().min(0).max(100),
  playerId: uuid.nullable(),
  playerSource: playerSource.nullable(),
  playerName: z.string().max(128).nullable(),
  identifiers: IdentifiersSchema.nullable(),
  coords: CoordsSchema.nullable(),
  evidence: jsonObject,
  samples: z.array(jsonObject).max(500).nullable(),
  screenshotId: uuid.nullable(),
  banId: uuid.nullable(),
  /** Agent clock, clamped to ±5 min of `receivedAt`. */
  occurredAt: isoDateTime,
  receivedAt: isoDateTime,
  reviewedBy: uuid.nullable(),
  reviewedAt: isoDateTime.nullable(),
  reviewNote: z.string().max(2000).nullable(),
  falsePositive: z.boolean(),
})
export type DetectionDto = z.infer<typeof DetectionDtoSchema>

/**
 * API.md §9 `GET …/detections`. `detectionKey`, `severity` and `action` are
 * repeatable and OR-combine inside their own group; groups AND together.
 * Changing a filter must restart with `cursor` omitted.
 */
export const DetectionListQuerySchema = ListQuerySchema.extend(DateRangeQuerySchema.shape).extend({
  detectionKey: repeatable(DetectionKeySchema).optional(),
  side: SideSchema.optional(),
  severity: repeatable(SeveritySchema).optional(),
  action: repeatable(ActionSchema).optional(),
  playerId: uuid.optional(),
  serverConfirmed: boolParam.optional(),
  falsePositive: boolParam.optional(),
  reviewed: boolParam.optional(),
  /** Player name or identifier value substring. */
  q: z.string().max(160).optional(),
})
export type DetectionListQuery = z.infer<typeof DetectionListQuerySchema>

/** API.md §9 `GET …/detections/summary` query. */
export const DetectionSummaryQuerySchema = z.object({
  range: StatsRangeSchema.default('24h'),
  playerId: uuid.optional(),
  detectionKey: DetectionKeySchema.optional(),
})
export type DetectionSummaryQuery = z.infer<typeof DetectionSummaryQuerySchema>

const countBucket = z.number().int().min(0).max(Number.MAX_SAFE_INTEGER)

export const DetectionKeyCountSchema = z.object({ detectionKey: DetectionKeySchema, count: countBucket })
export const SeverityCountSchema = z.object({ severity: SeveritySchema, count: countBucket })
export const ActionCountSchema = z.object({ action: ActionSchema, count: countBucket })
export const TimeBucketCountSchema = z.object({ t: isoDateTime, count: countBucket })
export type DetectionKeyCount = z.infer<typeof DetectionKeyCountSchema>
export type SeverityCount = z.infer<typeof SeverityCountSchema>
export type ActionCount = z.infer<typeof ActionCountSchema>
export type TimeBucketCount = z.infer<typeof TimeBucketCountSchema>

/** API.md §9 `GET …/detections/summary` — the dashboard aggregation, not a list. */
export const DetectionSummarySchema = z.object({
  range: StatsRangeSchema,
  bucket: StatsBucketSchema,
  total: countBucket,
  byKey: z.array(DetectionKeyCountSchema).max(DETECTION_KEYS.length),
  bySeverity: z.array(SeverityCountSchema).max(4),
  byAction: z.array(ActionCountSchema).max(3),
  byBucket: z.array(TimeBucketCountSchema).max(200),
})
export type DetectionSummary = z.infer<typeof DetectionSummarySchema>

/** API.md §9 `PATCH …/detections/:detectionId`. `falsePositive: true` dismisses; `false` + note resolves. */
export const DetectionReviewRequestSchema = z
  .strictObject({
    falsePositive: z.boolean().optional(),
    reviewNote: z.string().max(2000).nullable().optional(),
  })
  .refine((v) => v.falsePositive !== undefined || v.reviewNote !== undefined, {
    message: 'At least one field must be provided',
  })
export type DetectionReviewRequest = z.infer<typeof DetectionReviewRequestSchema>

/** API.md §9 `POST …/detections/bulk-review` — at most 200 ids, per-id outcomes. */
export const DetectionBulkReviewRequestSchema = z.strictObject({
  detectionIds: z.array(uuid).min(1).max(200),
  falsePositive: z.boolean(),
  reviewNote: z.string().max(2000).optional(),
})
export type DetectionBulkReviewRequest = z.infer<typeof DetectionBulkReviewRequestSchema>

export const DetectionBulkReviewResponseSchema = z.object({
  updated: z.number().int().min(0).max(200),
  skipped: z.array(z.object({ detectionId: uuid, reason: z.string().max(64) })).max(200),
})
export type DetectionBulkReviewResponse = z.infer<typeof DetectionBulkReviewResponseSchema>

// ─────────────────────────────── §10. Players ────────────────────────────────
// API.md §10 — `routes/panel/players.ts`. Default ordering `last_seen_at DESC`.

/** API.md §10 `GET …/players`. `primaryIdentifier` is the `type:value` display form. */
export const PlayerDtoSchema = z.object({
  id: uuid,
  serverId: uuid,
  primaryIdentifier: identifierExpr,
  name: z.string().max(128),
  identifiers: IdentifiersSchema,
  /** Opaque per-server `GetPlayerTokens` strings, rendered collapsed in the panel. */
  tokens: TokensSchema,
  lastSource: playerSource.nullable(),
  lastIp: ipAddress.nullable(),
  firstSeenAt: isoDateTime,
  lastSeenAt: isoDateTime,
  lastJoinAt: isoDateTime.nullable(),
  playSeconds: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
  joinCount: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
  detectionCount: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
  banCount: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
  /** 0–100, 50 for a new player; `connect.minTrustScore` gates joins against it. */
  trustScore: z.number().int().min(0).max(100),
  whitelisted: z.boolean(),
  online: z.boolean(),
  notes: z.string().max(4000).nullable(),
})
export type PlayerDto = z.infer<typeof PlayerDtoSchema>

/** Sort keys accepted by `GET …/players` (API.md §10). */
export const PLAYER_SORTS = ['lastSeen', 'trust', 'detections', 'playSeconds'] as const
export const PlayerSortSchema = z.enum(PLAYER_SORTS)
export type PlayerSort = z.infer<typeof PlayerSortSchema>

/** API.md §10 `GET …/players`. `identifier` is an exact `type:value`, e.g. `discord:1234567890`. */
export const PlayerListQuerySchema = ListQuerySchema.extend({
  online: boolParam.optional(),
  /** Name or any identifier value. */
  q: z.string().max(160).optional(),
  identifier: identifierExpr.optional(),
  whitelisted: boolParam.optional(),
  minTrustScore: z.coerce.number().int().min(0).max(100).optional(),
  maxTrustScore: z.coerce.number().int().min(0).max(100).optional(),
  sort: PlayerSortSchema.default('lastSeen'),
})
export type PlayerListQuery = z.infer<typeof PlayerListQuerySchema>

/** One `player_identifiers` row; `type` may be `token`, which never appears inside `Identifiers`. */
export const PlayerIdentifierDtoSchema = z.object({
  type: PlayerIdentifierTypeSchema,
  value: z.string().max(128),
  firstSeenAt: isoDateTime,
  lastSeenAt: isoDateTime,
})
export type PlayerIdentifierDto = z.infer<typeof PlayerIdentifierDtoSchema>

/** One `player_sessions` row (API.md §10). `id` is a bigserial rendered as a JSON number. */
export const PlayerSessionDtoSchema = z.object({
  id: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
  source: playerSource.nullable(),
  joinedAt: isoDateTime,
  leftAt: isoDateTime.nullable(),
  durationSeconds: z.number().int().min(0).max(31_536_000).nullable(),
  ip: ipAddress.nullable(),
  dropReason: z.string().max(500).nullable(),
  clientDropReason: z.number().int().min(0).max(65535).nullable(),
})
export type PlayerSessionDto = z.infer<typeof PlayerSessionDtoSchema>

/** Cross-server presence, limited to servers the caller is a member of (API.md §10). */
export const PlayerOtherServerSchema = z.object({
  serverId: uuid,
  serverName: z.string().max(80),
  lastSeenAt: isoDateTime,
  banCount: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
})
export type PlayerOtherServer = z.infer<typeof PlayerOtherServerSchema>

/** API.md §10 `GET …/players/:playerId`. `detections` is the last 25 rows. */
export const PlayerDetailDtoSchema = z.object({
  player: PlayerDtoSchema,
  identifiers: z.array(PlayerIdentifierDtoSchema).max(64),
  sessions: z.array(PlayerSessionDtoSchema).max(100),
  detections: z.array(DetectionDtoSchema).max(100),
  bans: z.array(z.lazy(() => BanDtoSchema)).max(100),
  screenshots: z.array(z.lazy(() => ScreenshotDtoSchema)).max(100),
  whitelist: z.lazy(() => WhitelistDtoSchema).nullable(),
  otherServers: z.array(PlayerOtherServerSchema).max(200),
})
export type PlayerDetailDto = z.infer<typeof PlayerDetailDtoSchema>

/** API.md §10 `PATCH …/players/:playerId` — notes are capped at 4000 characters. */
export const PlayerUpdateRequestSchema = z.strictObject({
  notes: z.string().max(4000).nullable(),
})
export type PlayerUpdateRequest = z.infer<typeof PlayerUpdateRequestSchema>

export const PlayerResponseSchema = z.object({ player: PlayerDtoSchema })
export type PlayerResponse = z.infer<typeof PlayerResponseSchema>

/** API.md §10 `GET …/players/:playerId/trust` — the breakdown behind `TrustScoreBar`. */
export const TrustFactorSchema = z.object({
  key: z.string().max(64),
  delta: z.number().int().min(-100).max(100),
  detail: z.string().max(200),
})
export type TrustFactor = z.infer<typeof TrustFactorSchema>

export const PlayerTrustResponseSchema = z.object({
  playerId: uuid,
  trustScore: z.number().int().min(0).max(100),
  factors: z.array(TrustFactorSchema).max(32),
  computedAt: isoDateTime,
})
export type PlayerTrustResponse = z.infer<typeof PlayerTrustResponseSchema>

// ──────────────────────────────── §11. Bans ──────────────────────────────────
// API.md §11 — this route and `POST /v1/agent/bans` are the only ban-creation
// paths. Detection ingest never creates a ban row.

/** Cloud-minted `SAC-` + 8 base32 characters (SPEC §8.7). */
const banCode = z.string().regex(/^SAC-[0-9A-Z]{8}$/)
/** Provisional `SAC-L…` code the agent showed the player before the cloud minted `banCode`. */
const agentBanCode = z.string().regex(/^SAC-[0-9A-Z]{1,16}$/)

/** API.md §11 `GET …/bans`. `q` matches either `banCode` or `agentBanCode`. */
export const BanDtoSchema = z.object({
  id: uuid,
  banCode,
  agentBanCode: agentBanCode.nullable(),
  serverId: uuid,
  playerId: uuid.nullable(),
  playerName: z.string().max(128).nullable(),
  reason: reasonText,
  /** The detection row uuid this ban came from, if any. */
  detectionId: uuid.nullable(),
  detectionKey: DetectionKeySchema.nullable(),
  source: BanSourceSchema,
  issuedByUserId: uuid.nullable(),
  /** `SentinelAC`, a staff display name, the invoking resource, or `txAdmin`. */
  issuedByLabel: z.string().max(80),
  identifiers: IdentifiersSchema,
  tokens: TokensSchema,
  /** Only matched at connect when `config.connect.banByIp` is true. */
  ip: ipAddress.nullable(),
  /** `null` = permanent. */
  expiresAt: isoDateTime.nullable(),
  isGlobal: z.boolean(),
  globalStatus: GlobalStatusSchema,
  corroborations: z.number().int().min(0).max(100_000),
  screenshotId: uuid.nullable(),
  active: z.boolean(),
  revokedAt: isoDateTime.nullable(),
  revokedBy: uuid.nullable(),
  revokeReason: reasonText.nullable(),
  createdAt: isoDateTime,
})
export type BanDto = z.infer<typeof BanDtoSchema>

export const BanResponseSchema = z.object({ ban: BanDtoSchema })
export type BanResponse = z.infer<typeof BanResponseSchema>

/** `status` filter of `GET …/bans` — `all` includes expired and revoked rows. */
export const BAN_LIST_STATUSES = ['active', 'expired', 'revoked', 'all'] as const
export const BanListStatusSchema = z.enum(BAN_LIST_STATUSES)
export type BanListStatus = z.infer<typeof BanListStatusSchema>

/** API.md §11 `GET …/bans`. `identifier` is an exact `type:value` matched against `ban_identifiers`. */
export const BanListQuerySchema = ListQuerySchema.extend(DateRangeQuerySchema.shape).extend({
  status: BanListStatusSchema.default('active'),
  source: BanSourceSchema.optional(),
  detectionKey: DetectionKeySchema.optional(),
  global: boolParam.optional(),
  /** Player name or either ban code. */
  q: z.string().max(160).optional(),
  identifier: identifierExpr.optional(),
})
export type BanListQuery = z.infer<typeof BanListQuerySchema>

/**
 * API.md §11 `POST …/bans`. Either `playerId` (online or historical) **or** a
 * raw `identifiers` object (offline ban by pasted identifiers); at least one
 * hard identifier must result. `durationSec: 0` or omitted = permanent.
 * Accepts an `Idempotency-Key` header, so a double-click cannot double-ban.
 */
export const BanCreateRequestSchema = z
  .strictObject({
    playerId: uuid.optional(),
    identifiers: IdentifiersSchema.optional(),
    playerName: z.string().max(128).optional(),
    reason: reasonText.min(1),
    durationSec: z.number().int().min(0).max(31_536_000).default(0),
    detectionId: uuid.optional(),
    screenshotId: uuid.optional(),
    proposeGlobal: z.boolean().default(false),
    /** Enqueues a `kick` command when the player is currently online. */
    kickNow: z.boolean().default(false),
  })
  .refine((v) => Boolean(v.playerId) !== Boolean(v.identifiers), {
    message: 'Exactly one of playerId or identifiers must be provided',
    path: ['playerId'],
  })
export type BanCreateRequest = z.infer<typeof BanCreateRequestSchema>

/** API.md §11 `PATCH …/bans/:banId`. `expiresAt: null` makes the ban permanent. */
export const BanUpdateRequestSchema = z
  .strictObject({
    expiresAt: isoDateTime.nullable().optional(),
    reason: reasonText.min(1).optional(),
  })
  .refine((v) => v.expiresAt !== undefined || v.reason !== undefined, {
    message: 'At least one field must be provided',
  })
export type BanUpdateRequest = z.infer<typeof BanUpdateRequestSchema>

/** API.md §11 `POST …/bans/:banId/revoke`. */
export const BanRevokeRequestSchema = z.strictObject({ reason: reasonText.min(1) })
export type BanRevokeRequest = z.infer<typeof BanRevokeRequestSchema>

/** API.md §11 `POST …/bans/import` — `data` is the raw exported payload, parsed server-side. */
export const BAN_IMPORT_FORMATS = ['txadmin', 'sentinelac', 'json'] as const
export const BanImportFormatSchema = z.enum(BAN_IMPORT_FORMATS)
export type BanImportFormat = z.infer<typeof BanImportFormatSchema>

export const BanImportRequestSchema = z.strictObject({
  format: BanImportFormatSchema,
  /** At most 20 000 entries once parsed; 1 MiB body limit applies. */
  data: z.string().min(1).max(1_048_576),
})
export type BanImportRequest = z.infer<typeof BanImportRequestSchema>

export const BanImportResponseSchema = z.object({
  imported: z.number().int().min(0).max(20_000),
  skipped: z.number().int().min(0).max(20_000),
  errors: z
    .array(z.object({ index: z.number().int().min(0).max(20_000), reason: z.string().max(200) }))
    .max(1000),
})
export type BanImportResponse = z.infer<typeof BanImportResponseSchema>

/** API.md §11 `GET …/bans/check` — one of `identifier` or `playerId`. */
export const BanCheckQuerySchema = z
  .object({
    identifier: identifierExpr.optional(),
    playerId: uuid.optional(),
  })
  .refine((v) => Boolean(v.identifier) !== Boolean(v.playerId), {
    message: 'Exactly one of identifier or playerId must be provided',
  })
export type BanCheckQuery = z.infer<typeof BanCheckQuerySchema>

/** The abbreviated global entry `GET …/bans/check` returns alongside the local answer. */
export const BanCheckGlobalRefSchema = z.object({
  id: uuid,
  globalStatus: GlobalStatusSchema,
  corroborations: z.number().int().min(0).max(100_000),
  /** True when the identifiers on the linked `GlobalBanDto` come back masked. */
  identifiersMasked: z.boolean(),
})
export type BanCheckGlobalRef = z.infer<typeof BanCheckGlobalRefSchema>

/** API.md §11 `GET …/bans/check` — "is this player banned here, and why", including history. */
export const BanCheckResponseSchema = z.object({
  matched: z.boolean(),
  activeBan: BanDtoSchema.nullable(),
  history: z.array(BanDtoSchema).max(200),
  globalBan: BanCheckGlobalRefSchema.nullable(),
  whitelisted: z.boolean(),
})
export type BanCheckResponse = z.infer<typeof BanCheckResponseSchema>

/** API.md §11 `GET …/bans/export` — served as a JSON attachment. */
export const BanExportResponseSchema = z.object({
  exportedAt: isoDateTime,
  serverId: uuid,
  bans: z.array(BanDtoSchema),
})
export type BanExportResponse = z.infer<typeof BanExportResponseSchema>

/** One `ban_identifiers` row (API.md §11 evidence view). */
export const BanIdentifierDtoSchema = z.object({
  type: PlayerIdentifierTypeSchema,
  value: z.string().max(128),
  isHard: z.boolean(),
  active: z.boolean(),
})
export type BanIdentifierDto = z.infer<typeof BanIdentifierDtoSchema>

/** One `global_ban_reviews` row (API.md §12). */
export const GlobalBanReviewDtoSchema = z.object({
  id: uuid,
  reviewerUserId: uuid.nullable(),
  decision: z.enum(['approved', 'rejected', 'revoked']),
  note: shortText.nullable(),
  createdAt: isoDateTime,
})
export type GlobalBanReviewDto = z.infer<typeof GlobalBanReviewDtoSchema>

/** API.md §11 `GET …/bans/:banId` — the evidence view. */
export const BanDetailResponseSchema = z.object({
  ban: BanDtoSchema,
  detection: DetectionDtoSchema.nullable(),
  screenshot: z.lazy(() => ScreenshotDtoSchema).nullable(),
  identifiers: z.array(BanIdentifierDtoSchema).max(64),
  reviews: z.array(GlobalBanReviewDtoSchema).max(100),
})
export type BanDetailResponse = z.infer<typeof BanDetailResponseSchema>

// ───────────────────────────── §12. Global bans ──────────────────────────────
// API.md §12 / SPEC §10.6 — only hard identifiers match globally (never ip,
// never tokens) and enforcement is opt-in per server.

/**
 * API.md §12. For everyone except platform admins and the proposing server's
 * owner the identifier **values are masked** to their first six characters plus
 * `…`; `identifiersMasked` says which form you received, and the unmasked
 * values are never returned to anyone else. The reporter is always anonymised
 * as `sunucu #<hash6>` in `proposerLabel` — the real server is never disclosed.
 */
export const GlobalBanSummarySchema = z.object({
  id: uuid,
  banCode,
  reason: reasonText,
  detectionKey: DetectionKeySchema.nullable(),
  globalStatus: GlobalStatusSchema,
  corroborations: z.number().int().min(0).max(100_000),
  identifiers: IdentifiersSchema,
  identifiersMasked: z.boolean(),
  proposerLabel: z.string().max(64),
  proposedAt: isoDateTime,
})
export type GlobalBanSummary = z.infer<typeof GlobalBanSummarySchema>

/** API.md §12 `GET /v1/global-bans` — the summary plus the review outcome. */
export const GlobalBanDtoSchema = GlobalBanSummarySchema.extend({
  hasScreenshot: z.boolean(),
  reviewedAt: isoDateTime.nullable(),
  /** `"platform"` or a reviewer label — never a reviewer's identity for tenants. */
  reviewedBy: z.string().max(64).nullable(),
  note: shortText.nullable(),
  /** Set when the caller's own server has an active override for this entry. */
  overriddenOnServer: uuid.nullable(),
})
export type GlobalBanDto = z.infer<typeof GlobalBanDtoSchema>

/** API.md §12 `GET /v1/global-bans`. `?status=proposed` is the platform-admin review queue. */
export const GlobalBanListQuerySchema = ListQuerySchema.extend({
  status: z.enum(['proposed', 'approved', 'rejected', 'revoked']).default('approved'),
  detectionKey: DetectionKeySchema.optional(),
  q: z.string().max(160).optional(),
})
export type GlobalBanListQuery = z.infer<typeof GlobalBanListQuerySchema>

/** API.md §12 `GET /v1/global-bans/check`. A soft type (`ip`/`token`) is a `VALIDATION_ERROR`. */
export const GlobalBanCheckQuerySchema = z.object({ identifier: identifierExpr })
export type GlobalBanCheckQuery = z.infer<typeof GlobalBanCheckQuerySchema>

export const GlobalBanEnforcementSchema = z.object({
  serverId: uuid,
  serverName: z.string().max(80),
  mode: GlobalBansModeSchema,
  overridden: z.boolean(),
})
export type GlobalBanEnforcement = z.infer<typeof GlobalBanEnforcementSchema>

/** API.md §12 `GET /v1/global-bans/check` — "why did a global ban block my player?". */
export const GlobalBanCheckResponseSchema = z.object({
  matched: z.boolean(),
  globalBan: GlobalBanSummarySchema.extend({ hasScreenshot: z.boolean() }).nullable(),
  enforcedOn: z.array(GlobalBanEnforcementSchema).max(200),
})
export type GlobalBanCheckResponse = z.infer<typeof GlobalBanCheckResponseSchema>

/** API.md §12 `POST /v1/global-bans/:banId/review` — platform admin only. */
export const GlobalBanReviewRequestSchema = z.strictObject({
  decision: z.enum(['approved', 'rejected', 'revoked']),
  note: shortText.optional(),
})
export type GlobalBanReviewRequest = z.infer<typeof GlobalBanReviewRequestSchema>

export const GlobalBanResponseSchema = z.object({ globalBan: GlobalBanDtoSchema })
export type GlobalBanResponse = z.infer<typeof GlobalBanResponseSchema>

/** API.md §12 `POST …/global-bans/:banId/override` — a per-ban exemption on one server. */
export const GlobalBanOverrideRequestSchema = z.strictObject({
  allow: z.boolean(),
  note: shortText.optional(),
})
export type GlobalBanOverrideRequest = z.infer<typeof GlobalBanOverrideRequestSchema>

export const GlobalBanOverrideDtoSchema = z.object({
  id: uuid,
  serverId: uuid,
  banId: uuid,
  allow: z.boolean(),
  createdBy: uuid.nullable(),
  createdAt: isoDateTime,
})
export type GlobalBanOverrideDto = z.infer<typeof GlobalBanOverrideDtoSchema>

export const GlobalBanOverrideResponseSchema = z.object({ override: GlobalBanOverrideDtoSchema })
export type GlobalBanOverrideResponse = z.infer<typeof GlobalBanOverrideResponseSchema>

// ────────────────── §13. Whitelist / bypass entries ──────────────────────────
// API.md §13 — `modules: null` means *all* modules (a full bypass, and with
// `bypassConnect` the connect ban check including global bans is skipped too).
// A `modules` array silences only those keys and never bypasses connect,
// whatever `bypassConnect` says. `identifierType` is hard types only.

export const WhitelistDtoSchema = z.object({
  id: uuid,
  serverId: uuid,
  identifierType: HardIdentifierTypeSchema,
  identifierValue: z.string().min(1).max(128),
  /** `null` = every module. */
  modules: z.array(DetectionKeySchema).max(DETECTION_KEYS.length).nullable(),
  bypassConnect: z.boolean(),
  label: label.nullable(),
  /** `null` = permanent. */
  expiresAt: isoDateTime.nullable(),
  createdBy: uuid.nullable(),
  createdAt: isoDateTime,
})
export type WhitelistDto = z.infer<typeof WhitelistDtoSchema>

export const WhitelistResponseSchema = z.object({ whitelist: WhitelistDtoSchema })
export type WhitelistResponse = z.infer<typeof WhitelistResponseSchema>

/** API.md §13 `POST …/whitelist`. Unique on `(server_id, identifier_type, identifier_value)`. */
export const WhitelistCreateRequestSchema = z.strictObject({
  identifierType: HardIdentifierTypeSchema,
  identifierValue: z.string().min(1).max(128),
  modules: z.array(DetectionKeySchema).max(DETECTION_KEYS.length).nullable(),
  bypassConnect: z.boolean().default(true),
  label: label.optional(),
  expiresAt: isoDateTime.nullable().optional(),
})
export type WhitelistCreateRequest = z.infer<typeof WhitelistCreateRequestSchema>

/** API.md §13 `POST …/whitelist/from-player` — one row per hard identifier the player is known by. */
export const WhitelistFromPlayerRequestSchema = z.strictObject({
  playerId: uuid,
  modules: z.array(DetectionKeySchema).max(DETECTION_KEYS.length).nullable(),
  bypassConnect: z.boolean().default(false),
  label: label.optional(),
  expiresAt: isoDateTime.nullable().optional(),
})
export type WhitelistFromPlayerRequest = z.infer<typeof WhitelistFromPlayerRequestSchema>

export const WhitelistFromPlayerResponseSchema = z.object({
  created: z.array(WhitelistDtoSchema).max(16),
  skipped: z.array(z.object({ identifierType: HardIdentifierTypeSchema, reason: z.string().max(64) })).max(16),
})
export type WhitelistFromPlayerResponse = z.infer<typeof WhitelistFromPlayerResponseSchema>

/** API.md §13 `PATCH …/whitelist/:whitelistId`. Sending `modules: null` widens the row to a full bypass. */
export const WhitelistUpdateRequestSchema = z
  .strictObject({
    modules: z.array(DetectionKeySchema).max(DETECTION_KEYS.length).nullable().optional(),
    bypassConnect: z.boolean().optional(),
    label: label.nullable().optional(),
    expiresAt: isoDateTime.nullable().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'At least one field must be provided' })
export type WhitelistUpdateRequest = z.infer<typeof WhitelistUpdateRequestSchema>

// ───────────────────────────── §14. Screenshots ──────────────────────────────
// API.md §14 — captures are evidence only; a missing screenshot is never a
// punishment signal (SPEC §8.13). No panel route ever receives image bytes.

export const ScreenshotDtoSchema = z.object({
  id: uuid,
  serverId: uuid,
  detectionId: uuid.nullable(),
  playerId: uuid.nullable(),
  playerSource: playerSource.nullable(),
  playerName: z.string().max(128).nullable(),
  status: ScreenshotStatusSchema,
  mime: z.enum(['image/jpeg', 'image/png', 'image/webp']).nullable(),
  bytes: z.number().int().min(0).max(5_242_880).nullable(),
  width: z.number().int().min(0).max(16_384).nullable(),
  height: z.number().int().min(0).max(16_384).nullable(),
  sha256: z.string().max(64).nullable(),
  /** `"timeout"` when a `pending` row aged past 180 s. */
  failReason: z.string().max(200).nullable(),
  requestedByUserId: uuid.nullable(),
  commandId: uuid.nullable(),
  /** Relative, authenticated path; `null` until the upload lands. */
  fileUrl: z.string().max(2048).nullable(),
  createdAt: isoDateTime,
  uploadedAt: isoDateTime.nullable(),
  expiresAt: isoDateTime,
})
export type ScreenshotDto = z.infer<typeof ScreenshotDtoSchema>

/** API.md §14 `GET …/screenshots`. */
export const ScreenshotListQuerySchema = ListQuerySchema.extend(DateRangeQuerySchema.shape).extend({
  playerId: uuid.optional(),
  detectionId: uuid.optional(),
  status: ScreenshotStatusSchema.optional(),
})
export type ScreenshotListQuery = z.infer<typeof ScreenshotListQuerySchema>

export const ScreenshotDetailResponseSchema = z.object({
  screenshot: ScreenshotDtoSchema,
  detection: DetectionDtoSchema.nullable(),
  ban: BanDtoSchema.nullable(),
})
export type ScreenshotDetailResponse = z.infer<typeof ScreenshotDetailResponseSchema>

/** API.md §14 `POST …/screenshots/:screenshotId/signed-url` — capped at 7 days. */
export const ScreenshotSignedUrlRequestSchema = z.strictObject({
  expiresInSec: z.number().int().min(60).max(604_800).default(604_800),
})
export type ScreenshotSignedUrlRequest = z.infer<typeof ScreenshotSignedUrlRequestSchema>

export const ScreenshotSignedUrlResponseSchema = z.object({
  url: z.url().max(2048),
  expiresAt: isoDateTime,
})
export type ScreenshotSignedUrlResponse = z.infer<typeof ScreenshotSignedUrlResponseSchema>

/** API.md §14 `POST …/screenshots` — creates the `pending` row and the `screenshot` command together. */
export const ScreenshotRequestRequestSchema = z.strictObject({
  playerSource,
  playerId: uuid.optional(),
})
export type ScreenshotRequestRequest = z.infer<typeof ScreenshotRequestRequestSchema>

export const ScreenshotRequestResponseSchema = z.object({
  command: z.lazy(() => CommandDtoSchema),
  screenshot: ScreenshotDtoSchema,
})
export type ScreenshotRequestResponse = z.infer<typeof ScreenshotRequestResponseSchema>

// ─────────────────────────────── §15. Commands ───────────────────────────────
// API.md §15 — commands ride the next heartbeat response and are acked in the
// one after. `expires_at` defaults to now + 10 min.

export const KickCommandPayloadSchema = z.object({ playerSource, reason: reasonText })
export const MessageCommandPayloadSchema = z.object({ playerSource: broadcastSource, text: z.string().max(500) })
export const ScreenshotCommandPayloadSchema = z.object({ playerSource, screenshotId: uuid })
export const BanCommandPayloadSchema = z.object({
  banCode,
  playerSource: playerSource.nullable(),
  reason: reasonText,
  expiresAt: isoDateTime.nullable(),
})
export const UnbanCommandPayloadSchema = z.object({ banCode })
export const ReloadConfigCommandPayloadSchema = z.object({})

/**
 * The `payload` column, keyed by the sibling `type` (API.md §15 table).
 * Ordered most-specific first so the empty `reloadConfig` payload cannot
 * shadow the others.
 */
export const CommandPayloadSchema = z.union([
  BanCommandPayloadSchema,
  UnbanCommandPayloadSchema,
  ScreenshotCommandPayloadSchema,
  KickCommandPayloadSchema,
  MessageCommandPayloadSchema,
  ReloadConfigCommandPayloadSchema,
])
export type CommandPayload = z.infer<typeof CommandPayloadSchema>

export const CommandDtoSchema = z.object({
  id: uuid,
  serverId: uuid,
  type: CommandTypeSchema,
  payload: CommandPayloadSchema,
  status: CommandStatusSchema,
  attempts: z.number().int().min(0).max(1000),
  createdByUserId: uuid.nullable(),
  createdByType: ActorTypeSchema,
  /** Echoed `Idempotency-Key`; a replay returns the original row with `200`. */
  idempotencyKey: z.string().max(128).nullable(),
  expiresAt: isoDateTime,
  deliveredAt: isoDateTime.nullable(),
  ackedAt: isoDateTime.nullable(),
  result: jsonObject.nullable(),
  error: z.string().max(500).nullable(),
  createdAt: isoDateTime,
})
export type CommandDto = z.infer<typeof CommandDtoSchema>

export const CommandResponseSchema = z.object({ command: CommandDtoSchema })
export type CommandResponse = z.infer<typeof CommandResponseSchema>

/**
 * API.md §15 `POST …/commands` — discriminated on `type`.
 *
 * `ban` and `unban` are deliberately absent: they are rejected here with a
 * `VALIDATION_ERROR` so every ban has exactly one creation path (`POST …/bans`
 * / `POST …/bans/:banId/revoke`) and always gets a row, a code and evidence
 * links. `allowQueue: true` skips the offline check and lets the command wait
 * until it expires instead of failing with `SERVER_OFFLINE`.
 */
export const CommandCreateRequestSchema = z.discriminatedUnion('type', [
  z.strictObject({
    type: z.literal('kick'),
    playerSource,
    reason: reasonText.min(1),
    allowQueue: z.boolean().default(false),
  }),
  z.strictObject({
    type: z.literal('message'),
    /** `-1` broadcasts to every player. */
    playerSource: broadcastSource,
    text: z.string().min(1).max(500),
    allowQueue: z.boolean().default(false),
  }),
  z.strictObject({
    type: z.literal('screenshot'),
    playerSource,
    playerId: uuid.optional(),
    allowQueue: z.boolean().default(false),
  }),
  z.strictObject({
    type: z.literal('reloadConfig'),
    allowQueue: z.boolean().default(false),
  }),
])
export type CommandCreateRequest = z.infer<typeof CommandCreateRequestSchema>

/** API.md §15 `GET …/commands`. */
export const CommandListQuerySchema = ListQuerySchema.extend({
  status: CommandStatusSchema.optional(),
  type: CommandTypeSchema.optional(),
})
export type CommandListQuery = z.infer<typeof CommandListQuerySchema>

/** API.md §11 `POST …/bans` and `…/bans/:banId/revoke` — the ban plus the queued agent command. */
export const BanCreateResponseSchema = z.object({
  ban: BanDtoSchema,
  /** The full `CommandDto`; API.md abbreviates the example to the fields the dialog reads. */
  command: CommandDtoSchema.nullable(),
})
export type BanCreateResponse = z.infer<typeof BanCreateResponseSchema>

export const BanRevokeResponseSchema = BanCreateResponseSchema
export type BanRevokeResponse = z.infer<typeof BanRevokeResponseSchema>

// ────────────────────────────── §16. Audit logs ──────────────────────────────
// API.md §16 — `action` is the closed list of SPEC §3.4; a filter value outside
// it is a `VALIDATION_ERROR`. `before`/`after` hold only the changed leaves and
// never contain secrets (webhook URLs, key material).

/** SPEC.md §3.4 — the closed audit action list. */
export const AUDIT_ACTIONS = [
  'auth.register',
  'auth.login',
  'auth.login_failed',
  'auth.locked',
  'auth.refresh_reuse',
  'auth.logout',
  'auth.password_change',
  'auth.password_reset',
  'apikey.create',
  'apikey.revoke',
  'server.create',
  'server.update',
  'server.delete',
  'server.transfer',
  'member.invite',
  'member.accept',
  'member.role',
  'member.remove',
  'license.create',
  'license.rotate',
  'license.revoke',
  'license.reset_ip',
  'license.bind_ip',
  'license.ip_mismatch',
  'license.update',
  'config.update',
  'config.rollback',
  'config.import',
  'config.reset',
  'ban.create',
  'ban.revoke',
  'ban.update',
  'ban.import',
  'ban.propose_global',
  'global_ban.review',
  'global_ban.override',
  'whitelist.add',
  'whitelist.update',
  'whitelist.remove',
  'command.create',
  'command.cancel',
  'webhook.create',
  'webhook.update',
  'webhook.delete',
  'webhook.test',
  'webhook.disabled',
  'detection.review',
  'player.notes',
  'screenshot.delete',
  'agent.auth',
  'agent.unlicensed',
] as const
export const AuditActionSchema = z.enum(AUDIT_ACTIONS)
export type AuditAction = z.infer<typeof AuditActionSchema>

export const AuditDtoSchema = z.object({
  /** `bigserial`, rendered as a JSON number. */
  id: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
  actorType: ActorTypeSchema,
  actorUserId: uuid.nullable(),
  actorApiKeyId: uuid.nullable(),
  actorLabel: z.string().max(254),
  serverId: uuid.nullable(),
  action: AuditActionSchema,
  targetType: z.string().max(64).nullable(),
  /** Not always a uuid — a config version is recorded as `"14"`. */
  targetId: z.string().max(128).nullable(),
  before: jsonObject.nullable(),
  after: jsonObject.nullable(),
  ip: ipAddress.nullable(),
  userAgent: z.string().max(500).nullable(),
  createdAt: isoDateTime,
})
export type AuditDto = z.infer<typeof AuditDtoSchema>

/** An exact action, or a `ban.*`-style prefix (API.md §16). */
export const AuditActionFilterSchema = z.union([AuditActionSchema, z.string().regex(/^[a-z_]+\.\*$/)])
export type AuditActionFilter = z.infer<typeof AuditActionFilterSchema>

export const AuditListQuerySchema = ListQuerySchema.extend(DateRangeQuerySchema.shape).extend({
  action: repeatable(AuditActionFilterSchema).optional(),
  actorType: ActorTypeSchema.optional(),
  actorUserId: uuid.optional(),
  targetType: z.string().max(64).optional(),
  targetId: z.string().max(128).optional(),
})
export type AuditListQuery = z.infer<typeof AuditListQuerySchema>

// ──────────────────────── §17. Discord webhooks ──────────────────────────────
// API.md §17 — the webhook URL is stored AES-256-GCM encrypted and is NEVER
// returned by any endpoint; only `urlHint` is. The game server never learns it
// either: `jobs/webhook-dispatcher.ts` is the only sender.

/** API.md §17 — the pattern the URL must match, re-validated server-side (`WEBHOOK_INVALID_URL`). */
export const DISCORD_WEBHOOK_URL_PATTERN = /^https:\/\/(ptb\.|canary\.)?discord(app)?\.com\/api\/webhooks\/\d+\/[\w-]+$/
const discordWebhookUrl = z.string().max(2048).regex(DISCORD_WEBHOOK_URL_PATTERN)

export const WebhookDtoSchema = z.object({
  id: uuid,
  serverId: uuid,
  name: label,
  /** `https://discord.com/api/webhooks/1234567890/****` — the full URL is never returned. */
  urlHint: z.string().max(2048),
  events: z.array(WebhookEventSchema).min(1).max(WEBHOOK_EVENTS.length),
  /** Filters `detection` events only. */
  minSeverity: SeveritySchema,
  mentionRoleId: z.string().max(32).nullable(),
  includeScreenshot: z.boolean(),
  enabled: z.boolean(),
  failureCount: z.number().int().min(0).max(100_000),
  /** Set when the dispatcher auto-disabled the webhook after a `401`/`404`. */
  disabledReason: z.string().max(200).nullable(),
  lastSuccessAt: isoDateTime.nullable(),
  lastFailureAt: isoDateTime.nullable(),
  lastError: z.string().max(500).nullable(),
  createdBy: uuid.nullable(),
  createdAt: isoDateTime,
  updatedAt: isoDateTime,
})
export type WebhookDto = z.infer<typeof WebhookDtoSchema>

export const WebhookResponseSchema = z.object({ webhook: WebhookDtoSchema })
export type WebhookResponse = z.infer<typeof WebhookResponseSchema>

/** API.md §17 `POST …/discord-webhooks`. `url` is write-only — responses carry `urlHint` instead. */
export const WebhookCreateRequestSchema = z.strictObject({
  name: label.min(1),
  url: discordWebhookUrl,
  events: z.array(WebhookEventSchema).min(1).max(WEBHOOK_EVENTS.length),
  minSeverity: SeveritySchema.default('medium'),
  mentionRoleId: z.string().max(32).nullable().optional(),
  includeScreenshot: z.boolean().default(true),
})
export type WebhookCreateRequest = z.infer<typeof WebhookCreateRequestSchema>

/**
 * API.md §17 `PATCH …/discord-webhooks/:webhookId`. Omitting `url` keeps the
 * stored one; re-enabling an auto-disabled webhook clears `disabledReason` and
 * `failureCount`.
 */
export const WebhookUpdateRequestSchema = z
  .strictObject({
    name: label.min(1).optional(),
    url: discordWebhookUrl.optional(),
    events: z.array(WebhookEventSchema).min(1).max(WEBHOOK_EVENTS.length).optional(),
    minSeverity: SeveritySchema.optional(),
    mentionRoleId: z.string().max(32).nullable().optional(),
    includeScreenshot: z.boolean().optional(),
    enabled: z.boolean().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'At least one field must be provided' })
export type WebhookUpdateRequest = z.infer<typeof WebhookUpdateRequestSchema>

/** API.md §17 `POST …/discord-webhooks/:webhookId/test` — reports Discord's own answer. */
export const WebhookTestResponseSchema = z.object({
  ok: z.boolean(),
  status: z.number().int().min(100).max(599).nullable(),
  error: z.string().max(500).nullable(),
  sentAt: isoDateTime,
})
export type WebhookTestResponse = z.infer<typeof WebhookTestResponseSchema>

/** API.md §17 `GET …/discord-webhooks/:webhookId/deliveries` — the `payload` column is never returned. */
export const WebhookDeliveryDtoSchema = z.object({
  id: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
  eventType: WebhookEventSchema,
  status: DeliveryStatusSchema,
  attempts: z.number().int().min(0).max(100),
  nextAttemptAt: isoDateTime,
  lastError: z.string().max(500).nullable(),
  createdAt: isoDateTime,
  sentAt: isoDateTime.nullable(),
})
export type WebhookDeliveryDto = z.infer<typeof WebhookDeliveryDtoSchema>

// ─────────────────────── §18. Stats and dashboard ────────────────────────────
// API.md §18 — `…/stats/overview` feeds the dashboard, `…/stats/health` the
// persistent StatusStrip (this is the "server status" read; there is no
// separate status route).

export const StatsRangeQuerySchema = z.object({ range: StatsRangeSchema.default('24h') })
export type StatsRangeQuery = z.infer<typeof StatsRangeQuerySchema>

export const StatsSeriesPointSchema = z.object({
  t: isoDateTime,
  players: z.number().int().min(0).max(4096),
  detections: countBucket,
  bans: countBucket,
})
export type StatsSeriesPoint = z.infer<typeof StatsSeriesPointSchema>

export const TopPlayerSchema = z.object({
  playerId: uuid,
  name: z.string().max(128),
  trustScore: z.number().int().min(0).max(100),
  detectionCount: countBucket,
})
export type TopPlayer = z.infer<typeof TopPlayerSchema>

/** API.md §18 `GET …/stats/overview`. The `24h` counters keep their names at every `range`. */
export const StatsOverviewSchema = z.object({
  range: StatsRangeSchema,
  bucket: StatsBucketSchema,
  playersOnline: z.number().int().min(0).max(4096),
  peakPlayers: z.number().int().min(0).max(4096),
  detections24h: countBucket,
  bans24h: countBucket,
  kicks24h: countBucket,
  bansTotal: countBucket,
  activeBans: countBucket,
  series: z.array(StatsSeriesPointSchema).max(200),
  byKey: z.array(DetectionKeyCountSchema).max(DETECTION_KEYS.length),
  bySeverity: z.array(SeverityCountSchema).max(4),
  byAction: z.array(ActionCountSchema).max(3),
  topPlayers: z.array(TopPlayerSchema).max(50),
})
export type StatsOverview = z.infer<typeof StatsOverviewSchema>

/** The license slice of the health read — status and binding only, never key material. */
export const StatsHealthLicenseSchema = z.object({
  status: LicenseStatusSchema,
  boundIp: ipAddress.nullable(),
  /** True when the newest `license.ip_mismatch` audit row is newer than `licenses.last_auth_at`. */
  ipMismatch: z.boolean(),
  lastAuthAt: isoDateTime.nullable(),
  graceUntil: isoDateTime.nullable(),
})
export type StatsHealthLicense = z.infer<typeof StatsHealthLicenseSchema>

/** API.md §18 `GET …/stats/health` — `status` flips to `offline` after 90 s without a heartbeat. */
export const StatsHealthSchema = z.object({
  status: ServerStatusSchema,
  lastHeartbeatAt: isoDateTime.nullable(),
  heartbeatAgeSec: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER).nullable(),
  uptimeSeconds: z.number().int().min(0).max(31_536_000),
  playersOnline: z.number().int().min(0).max(4096),
  maxPlayers: z.number().int().min(0).max(4096),
  tickMs: z.number().min(0).max(10_000).nullable(),
  latencyMs: z.number().int().min(0).max(600_000).nullable(),
  queueDepth: z.number().int().min(0).max(1_000_000).nullable(),
  agentVersion: z.string().max(32).nullable(),
  minAgentVersion: z.string().max(32),
  agentOutdated: z.boolean(),
  configVersion: z.number().int().min(0).max(1_000_000),
  agentConfigVersion: z.number().int().min(0).max(1_000_000),
  configSynced: z.boolean(),
  banListVersion: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
  resourceCount: z.number().int().min(0).max(100_000).nullable(),
  resourceHash: z.string().max(128).nullable(),
  agentIp: ipAddress.nullable(),
  license: StatsHealthLicenseSchema.nullable(),
})
export type StatsHealth = z.infer<typeof StatsHealthSchema>

// ─────────────────────────────── §19. Lookup ─────────────────────────────────
// API.md §19 — the Ctrl+K cross-server search, scoped to the servers the caller
// is a member of. 60/min per user.

/** `?identifier=discord:1234567890`; the panel normalises a pasted bare value first. */
export const LookupQuerySchema = z.object({ identifier: identifierExpr })
export type LookupQuery = z.infer<typeof LookupQuerySchema>

export const LookupPlayerHitSchema = z.object({
  serverId: uuid,
  serverName: z.string().max(80),
  player: PlayerDtoSchema,
})
export type LookupPlayerHit = z.infer<typeof LookupPlayerHitSchema>

/** API.md §19 `GET /v1/lookup`. `globalBan` is `null` when nothing matched and stays masked. */
export const LookupResponseSchema = z.object({
  identifier: z.object({ type: IdentifierTypeSchema, value: z.string().max(128) }),
  players: z.array(LookupPlayerHitSchema).max(200),
  /** Active bans on the caller's own servers only. */
  bans: z.array(BanDtoSchema).max(200),
  globalBan: GlobalBanSummarySchema.nullable(),
})
export type LookupResponse = z.infer<typeof LookupResponseSchema>

// ───────────────────── §20. Public and ops endpoints ─────────────────────────
// API.md §20 — `routes/public/**`, no membership required.

/** `GET /v1/healthz` — process liveness, no DB touch. */
export const HealthzResponseSchema = z.object({ ok: z.literal(true), version: z.string().max(32) })
export type HealthzResponse = z.infer<typeof HealthzResponseSchema>

/** `GET /v1/readyz` — `503` with `{ ok: false, db: null }` when the DB ping fails. */
export const ReadyzResponseSchema = z.object({
  ok: z.boolean(),
  db: z.enum(['pg', 'pglite']).nullable(),
})
export type ReadyzResponse = z.infer<typeof ReadyzResponseSchema>

// ─────────────────────────── Paginated envelopes ─────────────────────────────
// One per list endpoint (API.md §22), so a route handler can validate its own
// response without re-wrapping.

export const PaginatedApiKeysSchema = paginated(ApiKeyDtoSchema)
export const PaginatedServersSchema = paginated(ServerDtoSchema)
export const PaginatedMembersSchema = paginated(MemberDtoSchema)
export const PaginatedInvitesSchema = paginated(InviteDtoSchema)
export const PaginatedLicensesSchema = paginated(LicenseDtoSchema)
export const PaginatedConfigVersionsSchema = paginated(ConfigVersionSummarySchema)
export const PaginatedDetectionsSchema = paginated(DetectionDtoSchema)
export const PaginatedPlayersSchema = paginated(PlayerDtoSchema)
export const PaginatedBansSchema = paginated(BanDtoSchema)
export const PaginatedGlobalBansSchema = paginated(GlobalBanDtoSchema)
export const PaginatedWhitelistSchema = paginated(WhitelistDtoSchema)
export const PaginatedScreenshotsSchema = paginated(ScreenshotDtoSchema)
export const PaginatedCommandsSchema = paginated(CommandDtoSchema)
export const PaginatedAuditSchema = paginated(AuditDtoSchema)
export const PaginatedWebhooksSchema = paginated(WebhookDtoSchema)
export const PaginatedWebhookDeliveriesSchema = paginated(WebhookDeliveryDtoSchema)

/** API.md §9 `GET …/detections/:detectionId` — the row plus its joined evidence. */
export const DetectionDetailResponseSchema = z.object({
  detection: DetectionDtoSchema,
  player: PlayerDtoSchema.nullable(),
  screenshot: ScreenshotDtoSchema.nullable(),
  ban: BanDtoSchema.nullable(),
})
export type DetectionDetailResponse = z.infer<typeof DetectionDetailResponseSchema>

export const DetectionResponseSchema = z.object({ detection: DetectionDtoSchema })
export type DetectionResponse = z.infer<typeof DetectionResponseSchema>

/** API.md §5 `POST /v1/servers` — the server plus its first license, key included once. */
export const ServerCreateResponseSchema = z.object({
  server: ServerDtoSchema,
  license: LicenseRevealResponseSchema,
})
export type ServerCreateResponse = z.infer<typeof ServerCreateResponseSchema>
